import { NicknameSchema } from "@iec-id/shared/contracts/profile";
import { Input, Spinner } from "@iec-id/ui";
import { useForm } from "@tanstack/react-form";
import { useEffect, useRef, useState } from "react";
import { firstErrorKey } from "../form-errors";
import { useOnboardingT } from "../onboarding-i18n";
import { StepTitle } from "../step-title";
import { useNicknameCheck } from "../use-nickname-check";
import { useNicknameSuggestion } from "../use-nickname-suggestion";
import { StepForm, type StepProps } from "./step-shell";

/** Причина отказа сервера → ключ подписи под полем. */
const NICKNAME_REASON_KEYS = {
	taken: "identity.nicknameTaken",
	reserved: "identity.nicknameReserved",
	"too-short": "identity.nicknameTooShort",
	"too-long": "identity.nicknameTooLong",
	invalid: "identity.nicknameInvalid",
} as const;

/**
 * Шаг 1 — имя и никнейм (макет 4b-1).
 *
 * Фотография переехала на шаг «Немного о вас»: заглушка аватара зависит от
 * пола (решение владельца 2026-08-22), а пол спрашивают именно там. Показывать
 * картинку раньше вопроса значило подсовывать половине людей чужой пол.
 *
 * Занятость ника проверяется на лету, но кнопку она не блокирует: сеть может
 * молчать, а последнее слово всё равно за сервером на отправке. Блокировать
 * шаг из-за неотвеченного запроса — способ запереть человека в мастере.
 */
export function IdentityStep({ state, submit, pending, error }: StepProps) {
	const t = useOnboardingT();
	const { draft } = state;

	const form = useForm({
		defaultValues: {
			firstName: draft.firstName ?? "",
			nickname: draft.nickname ?? "",
		},
		onSubmit: ({ value }) => {
			submit({
				step: "identity",
				firstName: value.firstName.trim(),
				nickname: value.nickname.trim().toLowerCase(),
			});
		},
	});

	return (
		<>
			<StepTitle tooltip={t("identity.titleTooltip")}>{t("identity.title")}</StepTitle>

			<StepForm onSubmit={() => void form.handleSubmit()} pending={pending} error={error}>
				<form.Field
					name="firstName"
					validators={{
						onSubmit: ({ value }) => {
							const trimmed = value.trim();
							if (trimmed.length === 0) return "identity.errors.firstNameRequired";
							if (trimmed.length > 100) return "identity.errors.firstNameTooLong";
							return undefined;
						},
					}}
				>
					{(field) => {
						const key = firstErrorKey(field.state.meta.errors);
						return (
							<Input
								label={t("identity.firstName")}
								name="firstName"
								autoComplete="given-name"
								enterKeyHint="next"
								// Первое поле мастера — фокус приходит на него сразу.
								autoFocus
								value={field.state.value}
								disabled={pending}
								onChange={(event) => field.handleChange(event.target.value)}
								onBlur={field.handleBlur}
								{...(key ? { errorText: t(key) } : {})}
							/>
						);
					}}
				</form.Field>

				<form.Field
					name="nickname"
					validators={{
						onSubmit: ({ value }) => {
							const trimmed = value.trim();
							if (trimmed.length === 0) return "identity.errors.nicknameRequired";
							if (trimmed.length < 3) return "identity.nicknameTooShort";
							if (trimmed.length > 32) return "identity.nicknameTooLong";
							return NicknameSchema.safeParse(trimmed).success
								? undefined
								: "identity.nicknameInvalid";
						},
					}}
				>
					{(field) => (
						<NicknameField
							value={field.state.value}
							disabled={pending}
							errorKey={firstErrorKey(field.state.meta.errors)}
							onChange={field.handleChange}
							onBlur={field.handleBlur}
							suggestFrom={form.state.values.firstName}
						/>
					)}
				</form.Field>
			</StepForm>
		</>
	);
}

/**
 * Поле ника вынесено отдельно: хук проверки перерисовывается на каждый символ,
 * и держать его в теле шага значило бы гонять всю форму на каждое нажатие.
 */
function NicknameField({
	value,
	disabled,
	errorKey,
	onChange,
	onBlur,
	suggestFrom,
}: {
	value: string;
	disabled: boolean;
	errorKey: string | undefined;
	onChange: (next: string) => void;
	onBlur: () => void;
	/** Имя с соседнего поля — запасная база ника, если из почты его не собрать. */
	suggestFrom: string;
}) {
	const t = useOnboardingT();
	const check = useNicknameCheck(value);

	// Подставляем предложенный ник, пока человек поле не трогал. `touched`
	// взводится на первом же вводе и обратно не снимается: стёртый вручную ник
	// не должен возвращаться подсказкой.
	const [touched, setTouched] = useState(value.length > 0);
	const suggested = useNicknameSuggestion(suggestFrom, !touched);

	// `onChange` приходит от TanStack Form и в зависимости эффекта не годится:
	// новая ссылка на каждый рендер запускала бы подстановку по кругу.
	const applyRef = useRef(onChange);
	applyRef.current = onChange;

	useEffect(() => {
		if (touched || !suggested) return;
		applyRef.current(suggested);
	}, [touched, suggested]);

	const takenKey =
		check.result && !check.result.available && check.result.reason
			? NICKNAME_REASON_KEYS[check.result.reason]
			: undefined;
	// Ошибка формы важнее ответа сервера: она про то, что человек набрал прямо
	// сейчас, а ответ мог прийти на предыдущее значение.
	const shownErrorKey = errorKey ?? takenKey;

	const hint = shownErrorKey
		? undefined
		: check.pending
			? t("identity.nicknameChecking")
			: check.result?.available
				? t("identity.nicknameFree")
				: undefined;

	const suggestions = takenKey ? (check.result?.suggestions ?? []) : [];

	return (
		<div className="flex flex-col gap-1.5">
			<Input
				label={t("identity.nickname")}
				name="nickname"
				autoComplete="username"
				autoCapitalize="none"
				spellCheck={false}
				enterKeyHint="go"
				value={value}
				disabled={disabled}
				onChange={(event) => {
					setTouched(true);
					onChange(event.target.value);
				}}
				onBlur={onBlur}
				{...(check.pending ? { iconAfter: <Spinner size={16} /> } : {})}
				{...(shownErrorKey ? { errorText: t(shownErrorKey) } : {})}
				{...(hint ? { hint } : {})}
			/>
			{/* Свободные варианты живут в aria-live: они появляются в ответ на
			    набор, фокус при этом никуда не уезжает. */}
			{suggestions.length > 0 ? (
				<span role="status" aria-live="polite" className="t-meta text-fg-muted">
					{t("identity.nicknameSuggestions", { list: suggestions.join(", ") })}
				</span>
			) : null}
		</div>
	);
}

/** «Мария» → «МА»: две буквы, как во всей экосистеме. */
